// Who owns the text in the "Draft message to patient" box.
//
// That box is the literal SMS the patient receives, so slot edits must never
// silently replace words a person wrote. Rather than tracking yet another
// "touched" boolean — there are already two that disagree — decide from the
// text itself: the caller remembers the exact string it last wrote into the
// box, and we only overwrite while the box still holds that string.
//
// Unchanged means nobody typed, so regenerating is invisible and keeps the
// convenience for staff who only ever click slots. Anything else is a person's
// wording and is theirs.
export function canReplaceDraft(current: string, lastGenerated: string | null): boolean {
  const typed = String(current || '').trim();
  if (!typed) return true;
  // Nothing has been generated yet, so whatever is in the box came from a
  // person or from the AI seed they are working on. Either way, not ours.
  if (lastGenerated == null) return false;
  return typed === String(lastGenerated).trim();
}

export interface SchedulingState {
  status?: string | null;
  sentAt?: string | Date | null;
  requiresApproval?: boolean | null;
  /** 'slots' | 'no_slots' | 'confirmation' — what the AI drafted. */
  pendingType?: string | null;
  draftMessage?: string | null;
  message?: string | null;
}

// What belongs in the draft box for a task, given its scheduling state.
//
// The box was being refilled from the task after every send, because the task
// keeps the wording on `message` once it has gone out. To the office that reads
// as an unsent message still sitting there — one of the three symptoms behind
// "there is no confirmation that it was sent".
//
// A message that has gone to the patient belongs in the conversation, not in
// the box. Anything still awaiting approval belongs in the box, which is what
// keeps a refused send from losing somebody's wording.
export function draftForBox(scheduling: SchedulingState | null | undefined): string {
  const state = scheduling || {};
  // reject-draft writes status 'rejected' and leaves draftMessage where it was.
  // Without this, Discard would clear the box and the next refresh would put
  // the AI draft straight back.
  if (state.status === 'rejected') return '';
  // The AI's first draft for an appointment task lists the times it picked.
  // It is a suggestion the office may take with one press, not words waiting
  // in the box beside a list that filled itself. The confirmation draft —
  // the patient chose a time and the AI wrote the booking message — still
  // belongs in the box: that one books on Send.
  if (state.status === 'pending_approval' && (state.pendingType === 'slots' || state.pendingType === 'no_slots')) return '';
  const sent = !!state.sentAt && !state.requiresApproval;
  if (sent) return '';
  return String(state.draftMessage || state.message || '');
}
